import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import {
  ENVELOPE_STATUS,
  RECIPIENT_STATUS,
  PAYMENT_STATUS,
  SUBSCRIPTION_STATUS,
  CERTIFICATE_STATUS,
  TEMPLATE_STATUS,
  PLAN_TIERS,
  ROLES,
  getStatusConfig,
  shouldShowDot,
} from "./StatusConfig";

/**
 * Generic StatusBadge component with automatic dot indicators
 *
 * @param {string} status - The status key (e.g., "completed", "pending")
 * @param {string} type - The status type: "envelope" | "recipient" | "payment" | "subscription" | "certificate" | "template" | "plan" | "role"
 * @param {string} size - Badge size: "sm" | "default" | "lg"
 * @param {boolean} showDot - Force show/hide dot (auto-detected by default)
 * @param {string} className - Additional classes
 */
export const StatusBadge = ({
  status,
  type = "envelope",
  size = "default",
  showDot,
  className = "",
}) => {
  const { t } = useTranslation();

  const statusMaps = {
    envelope: ENVELOPE_STATUS,
    recipient: RECIPIENT_STATUS,
    payment: PAYMENT_STATUS,
    subscription: SUBSCRIPTION_STATUS,
    certificate: CERTIFICATE_STATUS,
    template: TEMPLATE_STATUS,
    plan: PLAN_TIERS,
    role: ROLES,
  };

  const statusMap = statusMaps[type] || statusMaps.envelope;
  const config = getStatusConfig(statusMap, status);

  // Auto-detect if dot should be shown based on variant
  const displayDot =
    showDot !== undefined ? showDot : shouldShowDot(config.variant);

  return (
    <Badge
      variant={config.variant}
      size={size}
      showDot={displayDot}
      className={className}
    >
      {t(config.labelKey, status)}
    </Badge>
  );
};

/**
 * Envelope-specific status badge
 */
export const EnvelopeStatusBadge = ({ status, size, showDot, className }) => (
  <StatusBadge
    status={status}
    type="envelope"
    size={size}
    showDot={showDot}
    className={className}
  />
);

/**
 * Recipient-specific status badge
 */
export const RecipientStatusBadge = ({ status, size, showDot, className }) => (
  <StatusBadge
    status={status}
    type="recipient"
    size={size}
    showDot={showDot}
    className={className}
  />
);

/**
 * Payment-specific status badge
 */
export const PaymentStatusBadge = ({ status, size, showDot, className }) => (
  <StatusBadge
    status={status}
    type="payment"
    size={size}
    showDot={showDot}
    className={className}
  />
);

/**
 * Subscription-specific status badge
 */
export const SubscriptionStatusBadge = ({
  status,
  size,
  showDot,
  className,
}) => (
  <StatusBadge
    status={status}
    type="subscription"
    size={size}
    showDot={showDot}
    className={className}
  />
);

/**
 * Certificate-specific status badge
 */
export const CertificateStatusBadge = ({
  status,
  size,
  showDot,
  className,
}) => (
  <StatusBadge
    status={status}
    type="certificate"
    size={size}
    showDot={showDot}
    className={className}
  />
);

/**
 * Plan tier badge (no dot by default)
 */
export const PlanBadge = ({ plan, size, className }) => (
  <StatusBadge
    status={plan}
    type="plan"
    size={size}
    showDot={false}
    className={className}
  />
);

/**
 * Role badge (no dot by default)
 */
export const RoleBadge = ({ role, size, className }) => (
  <StatusBadge
    status={role}
    type="role"
    size={size}
    showDot={false}
    className={className}
  />
);

export default StatusBadge;
